<template>
  <page-template :is-loading="isLoading" :data-exists="selectedProxyTemplateData !== null">
    <template #pageHeadline>
      {{
        isNew
          ? $t('proxyTemplate.configPage.newPageHeadline')
          : $t('proxyTemplate.configPage.editPageHeadline')
      }}
    </template>
    <template #secondLevelHeaderLeft>
      <rbz-form-button
        variant="tertiary"
        icon="IconArrowBack"
        v-b-tooltip="$t('common.operations.returnToList')"
        link-route-name="Proxy Templates List"
        classes="redirect-to-list-button"
        data-qa="redirect-to-list-button"
      />
      <template v-if="selectedProxyTemplate && !isNew">
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
        <rbz-dropdown
          :items="dropdownData.map((item) => [item.id, item.name] as [string, string])"
          :disabled="isLoading"
          v-model:value="selectedProxyTemplate"
          @update:value="switchProxyTemplate($event as string)"
          :ignore-update-in-process="true"
          type="single-select"
          :header-tooltip="$t('proxyTemplate.configPage.switchTooltip')"
          classes="switch-proxy-template switch-page-entry"
          data-qa="switch-proxy-template"
        />
      </template>
    </template>
    <template #secondLevelHeaderRight>
      <template v-if="selectedProxyTemplate && !isNew">
        <rbz-form-button
          :label="$t('common.operations.duplicate.label')"
          variant="tertiary"
          icon="IconDuplicate"
          @button-click="duplicateProxyTemplate()"
          class="duplicate-button"
          :disabled="isLoading || entityNotSaved"
          :tooltip="
            entityNotSaved ? $t('common.operations.duplicate.disabledTooltip.notSaved') : ''
          "
          data-qa="duplicate-button"
        />
        <rbz-form-button
          :label="$t('common.operations.download.label')"
          variant="tertiary"
          icon="IconDownload"
          @button-click="downloadProxyTemplate()"
          class="download-button"
          :disabled="isLoading"
          data-qa="download-button"
        />
        <rbz-form-button
          :label="$t('common.operations.delete.label')"
          variant="tertiary"
          icon="IconDelete"
          @button-click="openDeleteProxyTemplateModal()"
          class="delete-button"
          :disabled="isLoading || selectedProxyTemplateProtected || selectedProxyTemplateReferenced"
          :tooltip="deleteTooltip"
          data-qa="delete-button"
        />
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
      </template>
      <rbz-form-button
        :label="$t('common.operations.save.label')"
        :disabled="isLoading || !isProxyTemplateValid"
        @button-click="saveChanges()"
        class="save-changes-button"
        data-qa="save-changes-button"
      />
    </template>
    <template #pageContent>
      <div class="rbz-columns">
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-form-input
              name="name"
              classes="name"
              data-qa="name-input"
              :label="$t('proxyTemplate.configPage.name')"
              :placeholder="$t('proxyTemplate.configPage.name')"
              v-model:inputValue="selectedProxyTemplateData.name"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-textarea
              name="description"
              classes="description"
              data-qa="description-textarea"
              :label="$t('proxyTemplate.configPage.description')"
              v-model:inputValue="selectedProxyTemplateData.description"
              rows="2"
            />
          </div>
        </div>
      </div>
      <rbz-card-collapsible :is-collapsed="false" data-qa="frontend-settings-card">
        <template #title>
          {{ $t('proxyTemplate.configPage.frontendSettings.title') }}
          <span
            v-b-tooltip="$t('proxyTemplate.configPage.frontendSettings.warningTooltip')"
            class="info-icon-wrapper"
          >
            <icon-info-small color="tabasco" class="info-icon" />
          </span>
        </template>
        <template #content>
          <div class="settings-fields-flex-box">
            <div class="rbz-field settings-field">
              <rbz-form-textarea
                name="ip-header-name"
                classes="ip-header-name"
                data-qa="ip-header-name-input"
                :label="$t('proxyTemplate.configPage.frontendSettings.ipHeaderName')"
                :placeholder="$t('proxyTemplate.configPage.frontendSettings.ipHeaderName')"
                v-model:inputValue="xffHeaderNames"
                rows="5"
                @input="limitLines($event, 5)"
              >
                <template #postLabel>
                  <div class="rbz-field-info">
                    <b-tooltip target="tooltip-ip-header-name" triggers="hover click">
                      {{ $t('proxyTemplate.configPage.frontendSettings.ipHeaderNameHelper') }}
                    </b-tooltip>
                    <icon-info-small
                      color="dusty-gray"
                      id="tooltip-ip-header-name"
                      class="info-icon"
                    />
                  </div>
                </template>
              </rbz-form-textarea>
            </div>
            <div class="rbz-field settings-field">
              <rbz-form-input
                name="client-max-body-size"
                classes="client-max-body-size"
                data-qa="client-max-body-size-input"
                :label="$t('proxyTemplate.configPage.frontendSettings.clientMaxBodySize')"
                :placeholder="$t('proxyTemplate.configPage.frontendSettings.clientMaxBodySize')"
                suffix="mb"
                v-model:inputValue="selectedProxyTemplateData.client_max_body_size"
              >
                <template #postLabel>
                  <div class="rbz-field-info">
                    <b-tooltip target="tooltip-client-max-body-size" triggers="hover click">
                      {{ $t('proxyTemplate.configPage.frontendSettings.clientMaxBodySizeHelper') }}
                      <a
                        href="https://nginx.org/en/docs/http/ngx_http_core_module.html#client_max_body_size"
                        target="_blank"
                      >
                        {{ $t('proxyTemplate.configPage.moreInfo') }}
                      </a>
                    </b-tooltip>
                    <icon-info-small
                      color="dusty-gray"
                      id="tooltip-client-max-body-size"
                      class="info-icon"
                    />
                  </div>
                </template>
              </rbz-form-input>
            </div>
            <div class="rbz-field settings-field">
              <rbz-form-input
                name="limit-req-rate"
                classes="limit-req-rate"
                data-qa="limit-req-rate-input"
                :label="$t('proxyTemplate.configPage.frontendSettings.limitRequestsRate')"
                :placeholder="$t('proxyTemplate.configPage.frontendSettings.limitRequestsRate')"
                v-model:inputValue="selectedProxyTemplateData.limit_req_rate"
              >
                <template #postLabel>
                  <div class="rbz-field-info">
                    <b-tooltip target="tooltip-limit-req-rate" triggers="hover click">
                      {{ $t('proxyTemplate.configPage.frontendSettings.limitRequestsRateHelper') }}
                      <a href="https://www.nginx.com/blog/rate-limiting-nginx/" target="_blank">
                        {{ $t('proxyTemplate.configPage.moreInfo') }}
                      </a>
                    </b-tooltip>
                    <icon-info-small
                      color="dusty-gray"
                      id="tooltip-limit-req-rate"
                      class="info-icon"
                    />
                  </div>
                </template>
              </rbz-form-input>
            </div>
            <div class="rbz-field settings-field">
              <rbz-form-input
                name="limit-req-burst"
                classes="limit-req-burst"
                data-qa="limit-req-burst-input"
                :label="$t('proxyTemplate.configPage.frontendSettings.limitRequestsBurst')"
                :placeholder="$t('proxyTemplate.configPage.frontendSettings.limitRequestsBurst')"
                v-model:inputValue="selectedProxyTemplateData.limit_req_burst"
              >
                <template #postLabel>
                  <div class="rbz-field-info">
                    <b-tooltip target="tooltip-limit-req-burst" triggers="hover click">
                      {{ $t('proxyTemplate.configPage.frontendSettings.limitRequestsBurstHelper') }}
                      <a href="https://www.nginx.com/blog/rate-limiting-nginx/" target="_blank">
                        {{ $t('proxyTemplate.configPage.moreInfo') }}
                      </a>
                    </b-tooltip>
                    <icon-info-small
                      color="dusty-gray"
                      id="tooltip-limit-req-burst"
                      class="info-icon"
                    />
                  </div>
                </template>
              </rbz-form-input>
            </div>
            <div class="rbz-field settings-field">
              <rbz-form-input
                name="client-body-timeout"
                classes="client-body-timeout"
                data-qa="client-body-timeout-input"
                suffix="seconds"
                :label="$t('proxyTemplate.configPage.frontendSettings.clientBodyTimeout')"
                :placeholder="$t('proxyTemplate.configPage.frontendSettings.clientBodyTimeout')"
                v-model:inputValue="selectedProxyTemplateData.client_body_timeout"
              >
                <template #postLabel>
                  <div class="rbz-field-info">
                    <b-tooltip target="tooltip-client-body-timeout" triggers="hover click">
                      {{ $t('proxyTemplate.configPage.frontendSettings.clientBodyTimeoutHelper') }}
                      <a
                        href="https://nginx.org/en/docs/http/ngx_http_core_module.html#client_body_timeout"
                        target="_blank"
                      >
                        {{ $t('proxyTemplate.configPage.moreInfo') }}
                      </a>
                    </b-tooltip>
                    <icon-info-small
                      color="dusty-gray"
                      id="tooltip-client-body-timeout"
                      class="info-icon"
                    />
                  </div>
                </template>
              </rbz-form-input>
            </div>
            <div class="rbz-field settings-field">
              <rbz-form-input
                name="keepalive-timeout"
                classes="keepalive-timeout"
                data-qa="keepalive-timeout-input"
                :label="$t('proxyTemplate.configPage.frontendSettings.keepaliveTimeout')"
                :placeholder="$t('proxyTemplate.configPage.frontendSettings.keepaliveTimeout')"
                suffix="seconds"
                v-model:inputValue="selectedProxyTemplateData.keepalive_timeout"
              >
                <template #postLabel>
                  <div class="rbz-field-info">
                    <b-tooltip target="tooltip-keepalive-timeout" triggers="hover click">
                      {{ $t('proxyTemplate.configPage.frontendSettings.keepaliveTimeoutHelper') }}
                      <a
                        href="https://nginx.org/en/docs/http/ngx_http_core_module.html#keepalive_timeout"
                        target="_blank"
                      >
                        {{ $t('proxyTemplate.configPage.moreInfo') }}
                      </a>
                    </b-tooltip>
                    <icon-info-small
                      color="dusty-gray"
                      id="tooltip-keepalive-timeout"
                      class="info-icon"
                    />
                  </div>
                </template>
              </rbz-form-input>
            </div>
            <div class="rbz-field settings-field">
              <rbz-form-input
                name="client-header-timeout"
                classes="client-header-timeout"
                data-qa="client-header-timeout-input"
                suffix="seconds"
                :label="$t('proxyTemplate.configPage.frontendSettings.clientHeaderTimeout')"
                :placeholder="$t('proxyTemplate.configPage.frontendSettings.clientHeaderTimeout')"
                v-model:inputValue="selectedProxyTemplateData.client_header_timeout"
              >
                <template #postLabel>
                  <div class="rbz-field-info">
                    <b-tooltip target="tooltip-client-header-timeout" triggers="hover click">
                      {{
                        $t('proxyTemplate.configPage.frontendSettings.clientHeaderTimeoutHelper')
                      }}
                      <a
                        href="https://nginx.org/en/docs/http/ngx_http_core_module.html#client_header_timeout"
                        target="_blank"
                      >
                        {{ $t('proxyTemplate.configPage.moreInfo') }}
                      </a>
                    </b-tooltip>
                    <icon-info-small
                      color="dusty-gray"
                      id="tooltip-client-header-timeout"
                      class="info-icon"
                    />
                  </div>
                </template>
              </rbz-form-input>
            </div>
            <div class="rbz-field settings-field">
              <rbz-form-input
                name="send-timeout"
                classes="send-timeout"
                data-qa="send-timeout-input"
                :label="$t('proxyTemplate.configPage.frontendSettings.sendTimeout')"
                :placeholder="$t('proxyTemplate.configPage.frontendSettings.sendTimeout')"
                suffix="seconds"
                v-model:inputValue="selectedProxyTemplateData['send_timeout']"
              >
                <template #postLabel>
                  <div class="rbz-field-info">
                    <b-tooltip target="tooltip-send-timeout" triggers="hover click">
                      {{ $t('proxyTemplate.configPage.frontendSettings.sendTimeoutHelper') }}
                      <a
                        href="https://nginx.org/en/docs/http/ngx_http_core_module.html#send_timeout"
                        target="_blank"
                      >
                        {{ $t('proxyTemplate.configPage.moreInfo') }}
                      </a>
                    </b-tooltip>
                    <icon-info-small
                      color="dusty-gray"
                      id="tooltip-send-timeout"
                      class="info-icon"
                    />
                  </div>
                </template>
              </rbz-form-input>
            </div>
            <div class="rbz-field settings-field">
              <rbz-form-input
                name="large-header-size"
                classes="large-header-size"
                data-qa="large-header-size-input"
                suffix="kilobytes"
                :label="$t('proxyTemplate.configPage.frontendSettings.largeHeaderSize')"
                :placeholder="$t('proxyTemplate.configPage.frontendSettings.largeHeaderSize')"
                v-model:inputValue="selectedProxyTemplateData.large_client_header_buffers_size"
              >
                <template #postLabel>
                  <div class="rbz-field-info">
                    <b-tooltip target="tooltip-large-header-size" triggers="hover click">
                      {{ $t('proxyTemplate.configPage.frontendSettings.largeHeaderSizeHelper') }}
                      <a
                        href="https://nginx.org/en/docs/http/ngx_http_core_module.html#large_client_header_buffers"
                        target="_blank"
                      >
                        {{ $t('proxyTemplate.configPage.moreInfo') }}
                      </a>
                    </b-tooltip>
                    <icon-info-small
                      color="dusty-gray"
                      id="tooltip-large-header-size"
                      class="info-icon"
                    />
                  </div>
                </template>
              </rbz-form-input>
            </div>
            <div class="rbz-field settings-field">
              <rbz-form-input
                name="header-buffer-size"
                classes="header-buffer-size"
                data-qa="header-buffer-size-input"
                :label="$t('proxyTemplate.configPage.frontendSettings.headerBufferSize')"
                :placeholder="$t('proxyTemplate.configPage.frontendSettings.headerBufferSize')"
                suffix="kilobytes"
                v-model:inputValue="selectedProxyTemplateData.client_header_buffer_size"
              >
                <template #postLabel>
                  <div class="rbz-field-info">
                    <b-tooltip target="tooltip-header-buffer-size" triggers="hover click">
                      {{ $t('proxyTemplate.configPage.frontendSettings.headerBufferSizeHelper') }}
                      <a
                        href="https://nginx.org/en/docs/http/ngx_http_core_module.html#client_header_buffer_size"
                        target="_blank"
                      >
                        {{ $t('proxyTemplate.configPage.moreInfo') }}
                      </a>
                    </b-tooltip>
                    <icon-info-small
                      color="dusty-gray"
                      id="tooltip-header-buffer-size"
                      class="info-icon"
                    />
                  </div>
                </template>
              </rbz-form-input>
            </div>
            <div class="rbz-field settings-field">
              <rbz-form-input
                name="large-header-buffers"
                classes="large-header-buffers"
                data-qa="large-header-buffers-input"
                :label="$t('proxyTemplate.configPage.frontendSettings.largeHeaderBuffers')"
                :placeholder="$t('proxyTemplate.configPage.frontendSettings.largeHeaderBuffers')"
                v-model:inputValue="selectedProxyTemplateData.large_client_header_buffers_count"
              >
                <template #postLabel>
                  <div class="rbz-field-info">
                    <b-tooltip target="tooltip-large-header-buffers" triggers="hover click">
                      {{ $t('proxyTemplate.configPage.frontendSettings.largeHeaderBuffersHelper') }}
                      <a
                        href="https://nginx.org/en/docs/http/ngx_http_core_module.html#large_client_header_buffers"
                        target="_blank"
                      >
                        {{ $t('proxyTemplate.configPage.moreInfo') }}
                      </a>
                    </b-tooltip>
                    <icon-info-small
                      color="dusty-gray"
                      id="tooltip-large-header-buffers"
                      class="info-icon"
                    />
                  </div>
                </template>
              </rbz-form-input>
            </div>
            <div class="rbz-field settings-field">
              <rbz-form-input
                name="client-body-buffer-size"
                classes="client-body-buffer-size"
                data-qa="client-body-buffer-size-input"
                :label="$t('proxyTemplate.configPage.frontendSettings.clientBodyBufferSize')"
                :placeholder="$t('proxyTemplate.configPage.frontendSettings.clientBodyBufferSize')"
                :is-invalid="!clientBodyBufferSizeValid"
                suffix="mb"
                v-model:inputValue="selectedProxyTemplateData.client_body_buffer_size"
              >
                <template #postLabel>
                  <div class="rbz-field-info">
                    <b-tooltip target="tooltip-client-body-buffer-size" triggers="hover click">
                      {{
                        $t('proxyTemplate.configPage.frontendSettings.clientBodyBufferSizeHelper')
                      }}
                      <a
                        href="https://nginx.org/en/docs/http/ngx_http_core_module.html#client_body_buffer_size"
                        target="_blank"
                      >
                        {{ $t('proxyTemplate.configPage.moreInfo') }}
                      </a>
                    </b-tooltip>
                    <icon-info-small
                      color="dusty-gray"
                      id="tooltip-client-body-buffer-size"
                      class="info-icon"
                    />
                  </div>
                </template>
              </rbz-form-input>
            </div>
          </div>
        </template>
      </rbz-card-collapsible>
      <rbz-card-collapsible :is-collapsed="false" data-qa="backend-settings-card">
        <template #title>
          {{ $t('proxyTemplate.configPage.backendSettings.title') }}
        </template>
        <template #content>
          <div class="settings-fields-flex-box">
            <div class="rbz-field settings-field">
              <rbz-form-input
                name="proxy-connect-timeout"
                classes="proxy-connect-timeout"
                data-qa="proxy-connect-timeout-input"
                suffix="seconds"
                :label="$t('proxyTemplate.configPage.backendSettings.proxyConnectTimeout')"
                :placeholder="$t('proxyTemplate.configPage.backendSettings.proxyConnectTimeout')"
                v-model:inputValue="selectedProxyTemplateData.proxy_connect_timeout"
              >
                <template #postLabel>
                  <div class="rbz-field-info">
                    <b-tooltip target="tooltip-proxy-connect-timeout" triggers="hover click">
                      {{ $t('proxyTemplate.configPage.backendSettings.proxyConnectTimeoutHelper') }}
                      <a
                        href="https://nginx.org/en/docs/http/ngx_http_proxy_module.html#proxy_connect_timeout"
                        target="_blank"
                      >
                        {{ $t('proxyTemplate.configPage.moreInfo') }}
                      </a>
                    </b-tooltip>
                    <icon-info-small
                      color="dusty-gray"
                      id="tooltip-proxy-connect-timeout"
                      class="info-icon"
                    />
                  </div>
                </template>
              </rbz-form-input>
            </div>
            <div class="rbz-field settings-field">
              <rbz-form-input
                name="upstream-host"
                classes="upstream-host"
                data-qa="upstream-host-input"
                :label="$t('proxyTemplate.configPage.backendSettings.backendServiceHostHeader')"
                :placeholder="
                  $t('proxyTemplate.configPage.backendSettings.backendServiceHostHeader')
                "
                v-model:inputValue="selectedProxyTemplateData.upstream_host"
              >
                <template #postLabel>
                  <div class="rbz-field-info">
                    <b-tooltip target="tooltip-upstream-host" triggers="hover click">
                      {{
                        $t(
                          'proxyTemplate.configPage.backendSettings.backendServiceHostHeaderHelper'
                        )
                      }}
                    </b-tooltip>
                    <icon-info-small
                      color="dusty-gray"
                      id="tooltip-upstream-host"
                      class="info-icon"
                    />
                  </div>
                </template>
              </rbz-form-input>
            </div>
            <div class="rbz-field settings-field">
              <rbz-form-input
                name="proxy-send-timeout"
                classes="proxy-send-timeout"
                data-qa="proxy-send-timeout-input"
                :label="$t('proxyTemplate.configPage.backendSettings.proxySendTimeout')"
                :placeholder="$t('proxyTemplate.configPage.backendSettings.proxySendTimeout')"
                suffix="seconds"
                v-model:inputValue="selectedProxyTemplateData.proxy_send_timeout"
              >
                <template #postLabel>
                  <div class="rbz-field-info">
                    <b-tooltip target="tooltip-proxy-send-timeout" triggers="hover click">
                      {{ $t('proxyTemplate.configPage.backendSettings.proxySendTimeoutHelper') }}
                      <a
                        href="https://nginx.org/en/docs/http/ngx_http_proxy_module.html#proxy_send_timeout"
                        target="_blank"
                      >
                        {{ $t('proxyTemplate.configPage.moreInfo') }}
                      </a>
                    </b-tooltip>
                    <icon-info-small
                      color="dusty-gray"
                      id="tooltip-proxy-send-timeout"
                      class="info-icon"
                    />
                  </div>
                </template>
              </rbz-form-input>
            </div>
            <div class="rbz-field settings-field">
              <rbz-form-input
                name="real-ip-header-name"
                classes="real-ip-header-name"
                data-qa="real-ip-header-name-input"
                :label="$t('proxyTemplate.configPage.backendSettings.realIpHeaderName')"
                :placeholder="$t('proxyTemplate.configPage.backendSettings.realIpHeaderName')"
                v-model:inputValue="selectedProxyTemplateData.xrealip_header_name"
              >
                <template #postLabel>
                  <div class="rbz-field-info">
                    <b-tooltip target="tooltip-real-ip-header-name" triggers="hover click">
                      {{ $t('proxyTemplate.configPage.backendSettings.realIpHeaderNameHelper') }}
                    </b-tooltip>
                    <icon-info-small
                      color="dusty-gray"
                      id="tooltip-real-ip-header-name"
                      class="info-icon"
                    />
                  </div>
                </template>
              </rbz-form-input>
            </div>
            <div class="rbz-field settings-field">
              <rbz-form-input
                name="proxy-read-timeout"
                classes="proxy-read-timeout"
                data-qa="proxy-read-timeout-input"
                :label="$t('proxyTemplate.configPage.backendSettings.proxyReadTimeout')"
                :placeholder="$t('proxyTemplate.configPage.backendSettings.proxyReadTimeout')"
                suffix="seconds"
                v-model:inputValue="selectedProxyTemplateData.proxy_read_timeout"
              >
                <template #postLabel>
                  <div class="rbz-field-info">
                    <b-tooltip target="tooltip-proxy-read-timeout" triggers="hover click">
                      {{ $t('proxyTemplate.configPage.backendSettings.proxyReadTimeoutHelper') }}
                      <a
                        href="https://nginx.org/en/docs/http/ngx_http_proxy_module.html#proxy_read_timeout"
                        target="_blank"
                      >
                        {{ $t('proxyTemplate.configPage.moreInfo') }}
                      </a>
                    </b-tooltip>
                    <icon-info-small
                      color="dusty-gray"
                      id="tooltip-proxy-read-timeout"
                      class="info-icon"
                    />
                  </div>
                </template>
              </rbz-form-input>
            </div>
          </div>
        </template>
      </rbz-card-collapsible>
      <rbz-card-collapsible
        :is-collapsed="false"
        class="trusted-sources-card"
        data-qa="trusted-sources-card"
      >
        <template #title>
          {{ $t('proxyTemplate.configPage.trustedSources.title') }}
        </template>
        <template #content>
          <div class="content">
            <rbz-table
              :columns="trustedSourcesColumns"
              :table-data="trustedSourcesData"
              :row-buttons="trustedSourcesRowButtons"
              :show-new-button="true"
              :show-new-as-row="true"
              rows-per-page="20"
              data-qa="trusted-sources-table"
              ref="trustedSourcesTable"
              :show-new-default-data="defaultNewTrustedSource"
              :show-download-button="false"
              :show-filter-button="false"
              :sticky-header="false"
              @add-inline-data="addTrustedSource($event)"
              @edit-inline-data="editTrustedSource($event)"
              @delete-row-button-clicked="deleteTrustedSource($event)"
            >
              <template #newRowOverride="rowProps">
                <td>
                  <rbz-dropdown
                    :items="Object.entries(trustedSourcesTypes)"
                    v-model:value="rowProps.data.source"
                  />
                </td>
                <td>
                  <template v-if="['global_filter'].includes(rowProps.data.source)">
                    <rbz-dropdown
                      :items="getGlobalFiltersDropdownOptions(rowProps.data)"
                      v-model:value="rowProps.data.globalFilter"
                      classes="global-filter-dropdown"
                      data-qa="global-filter-dropdown"
                    />
                  </template>
                  <template v-else>
                    <rbz-form-input
                      name="address"
                      classes="address-value-input"
                      :placeholder="$t('proxyTemplate.configPage.trustedSources.ip')"
                      data-qa="address-value-input"
                      :is-invalid="!validateTrustedSourceIP(rowProps.data)"
                      :error-message-text="getErrorsTrustedSourceIP(rowProps.data)"
                      v-model:inputValue="rowProps.data.address"
                    />
                  </template>
                </td>
                <td>
                  <rbz-form-input
                    name="comment"
                    classes="comment-value-input"
                    :placeholder="$t('proxyTemplate.configPage.trustedSources.comment')"
                    data-qa="comment-value-input"
                    v-model:inputValue="rowProps.data.comment"
                  />
                </td>
              </template>
              <template #newRowOverrideConfirmationButtons="rowProps">
                <td class="row-buttons-cell">
                  <form-confirmation
                    @confirm="
                      rowProps.mode === 'new'
                        ? addTrustedSource(rowProps.data)
                        : editTrustedSource(rowProps.data)
                    "
                    :is-invalid="!!newTrustedSourceError(rowProps.data)"
                    @cancel="
                      rowProps.mode === 'new'
                        ? cancelAddTrustedSource()
                        : cancelEditTrustedSource(rowProps.data.id)
                    "
                  />
                </td>
              </template>
            </rbz-table>
          </div>
        </template>
      </rbz-card-collapsible>
      <rbz-card-collapsible
        :is-collapsed="true"
        class="advanced-configuration-card"
        data-qa="advanced-configuration-card"
      >
        <template #title>
          {{ $t('proxyTemplate.configPage.advancedConfig.title') }}
          <span
            v-b-tooltip="$t('proxyTemplate.configPage.advancedConfig.warningTooltip')"
            class="info-icon-wrapper"
          >
            <icon-info-small color="tabasco" class="info-icon" />
          </span>
        </template>
        <template #content>
          <div class="content">
            <rbz-table
              :columns="advancedConfigColumns"
              :table-data="selectedProxyTemplateData['advanced_configuration']"
              :row-buttons="advancedConfigRowButtons"
              :show-new-button="true"
              rows-per-page="20"
              data-qa="advanced-configuration-table"
              ref="advancedConfigurationTable"
              :show-download-button="false"
              :show-filter-button="false"
              :new-button-label="$t('proxyTemplate.configPage.advancedConfig.addNew')"
              :sticky-header="false"
              :show-expander-column="true"
              :persist-expander-data="true"
              @new-button-clicked="addAdvancedConfigEntry()"
              @delete-row-button-clicked="deleteAdvancedConfigEntry($event)"
            >
              <template #rowExpander="rowProps">
                <div class="rbz-columns">
                  <div class="rbz-column is-4">
                    <div class="rbz-field">
                      <rbz-form-input
                        name="advanced-config-entry-name"
                        data-qa="advanced-config-entry-name"
                        class="advanced-config-entry-name-input"
                        :ref="`advancedConfigurationEntryName${rowProps.row.id}`"
                        :label="$t('proxyTemplate.configPage.advancedConfig.nameFieldLabel')"
                        :placeholder="$t('proxyTemplate.configPage.advancedConfig.nameFieldLabel')"
                        v-model:inputValue="rowProps.row.name"
                        :is-invalid="!!advancedConfigEntryNameError(rowProps.row)"
                        :error-message-text="advancedConfigEntryNameError(rowProps.row)"
                      />
                    </div>
                    <div class="rbz-field">
                      <rbz-form-textarea
                        name="advanced-config-entry-description"
                        classes="advanced-config-entry-description"
                        data-qa="advanced-config-entry-description-textarea"
                        :label="$t('proxyTemplate.configPage.advancedConfig.description')"
                        v-model:inputValue="rowProps.row.description"
                        rows="2"
                      />
                    </div>
                  </div>
                  <div class="rbz-column is-4">
                    <div class="rbz-field">
                      <label
                        class="rbz-label rbz-label-list"
                        :class="{ disabled: planetStore.isUpdateInProcess }"
                      >
                        {{ $t('proxyTemplate.configPage.advancedConfig.protocol') }}
                        <span
                          v-b-tooltip="
                            $t(
                              'proxyTemplate.configPage.advancedConfig.protocolAdditionalInfoTooltip'
                            )
                          "
                          class="info-icon-wrapper"
                        >
                          <icon-info-small color="dusty-gray" class="info-icon is-vcentered" />
                        </span>
                      </label>
                      <div class="protocol-radio-wrapper">
                        <rbz-form-radio
                          :name="`entry-${rowProps.row.id}-protocol`"
                          :id="`entry-${rowProps.row.id}-protocol-http`"
                          label="HTTP"
                          @input="rowProps.row.protocol = ['http']"
                          :checked="isProtocolChecked(rowProps.row.protocol, ['http'])"
                        />
                        <rbz-form-radio
                          :name="`entry-${rowProps.row.id}-protocol`"
                          :id="`entry-${rowProps.row.id}-protocol-https`"
                          label="HTTPS"
                          @input="rowProps.row.protocol = ['https']"
                          :checked="isProtocolChecked(rowProps.row.protocol, ['https'])"
                        />
                        <rbz-form-radio
                          :name="`entry-${rowProps.row.id}-protocol`"
                          :id="`entry-${rowProps.row.id}-protocol-both`"
                          label="HTTP + HTTPS"
                          @input="rowProps.row.protocol = ['http', 'https']"
                          :checked="isProtocolChecked(rowProps.row.protocol, ['http', 'https'])"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="rbz-column is-4">
                    <div class="rbz-field">
                      <rbz-form-textarea
                        name="advanced-config-entry-configuration"
                        data-qa="advanced-config-entry-configuration-textarea"
                        class="advanced-config-entry-configuration"
                        :label="$t('proxyTemplate.configPage.advancedConfig.customConfiguration')"
                        v-model:inputValue="rowProps.row.configuration"
                        :is-invalid="!!advancedConfigEntryCodeError(rowProps.row)"
                        :error-message-text="advancedConfigEntryCodeError(rowProps.row)"
                        rows="6"
                      >
                        <template #postLabel>
                          <span
                            v-b-tooltip="
                              $t('proxyTemplate.configPage.advancedConfig.warningTooltip')
                            "
                            class="info-icon-wrapper"
                          >
                            <icon-info-small color="tabasco" class="info-icon" />
                          </span>
                        </template>
                      </rbz-form-textarea>
                    </div>
                  </div>
                </div>
              </template>
            </rbz-table>
          </div>
        </template>
      </rbz-card-collapsible>
      <rbz-modal-dialog
        :show="showDeleteProxyTemplateModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteProxyTemplate"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteProxyTemplateModal = false"
        @closeModal="showDeleteProxyTemplateModal = false"
        size="medium"
      >
        <template #content>
          {{
            $t('common.operations.delete.modal.message', { name: selectedProxyTemplateData.name })
          }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent, nextTick } from 'vue';
import {
  BasicDocument,
  DocumentName,
  ProxyTemplate,
  ProxyTemplateAdvancedConfigEntry,
  GlobalFilter,
  TrustedSource,
  ApiTrustedSource,
  ApiPlanet,
} from '@/types';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import RequestsUtils from '@/helpers/RequestsUtils';
import Utils from 'ui-component-library/helpers/Utils';
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzDropdown from '@/components/RbzDropdown.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import RbzFormTextarea from '@/components/RbzFormTextarea.vue';
import PageTemplate from '@/views/PageTemplate.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import RbzCardCollapsible from 'ui-component-library/rbz/components/RbzCardCollapsible.vue';
import FormConfirmation from 'ui-component-library/rbz/components/FormConfirmation.vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import ipaddr from 'ipaddr.js';
import IconInfoSmall from 'ui-component-library/assets/icons/IconInfoSmall.vue';
import RbzFormRadio from '@/components/RbzFormRadio.vue';
import { usePlanetStore } from '@/store/PlanetStore';
import {
  RbzTableColumnOptions,
  RbzTableRowButton,
  GenericObject,
} from 'ui-component-library/rbz/types';
import { ENV } from '@/constants';
import { useRouting } from '@/helpers/RoutingUtils';

export default defineComponent({
  name: 'ProxyTemplatesEditor',
  setup() {
    const { getEditPath, getListPath, navigateTo, normalizeRouteName } = useRouting();
    return {
      getEditPath,
      getListPath,
      navigateTo,
      normalizeRouteName,
    };
  },
  components: {
    IconInfoSmall,
    RbzFormButton,
    RbzFormInput,
    RbzFormRadio,
    RbzFormTextarea,
    LayoutDividerLine,
    PageTemplate,
    RbzCardCollapsible,
    RbzDropdown,
    RbzModalDialog,
    RbzTable,
    FormConfirmation,
  },
  data() {
    const trustedSourcesTypes: Record<string, string> = {
      ip: this.$t('keyOption.ip'),
      global_filter: this.$t('keyOption.globalFilter'),
    };
    const defaultTrustedSourcesType = 'ip';
    return {
      dropdownData: [] as BasicDocument[],
      isNew: false,
      selectedProxyTemplate: null as string,
      selectedProxyTemplateData: null as ProxyTemplate,
      originalProxyTemplateData: null as ProxyTemplate,
      pageLoadingCounter: 0,
      isDeleteLoading: false,
      showDeleteProxyTemplateModal: false,

      referencedIds: [] as string[],
      globalFilters: [] as GlobalFilter[],
      planet: null as ApiPlanet,
      defaultTrustedSourcesType: defaultTrustedSourcesType,
      defaultNewTrustedSource: {
        source: defaultTrustedSourcesType,
        address: '',
        globalFilter: '',
        comment: '',
      } as TrustedSource,
      trustedSourcesData: [] as TrustedSource[],
      originalTrustedSourcesData: [] as TrustedSource[],
      trustedSourcesTypes: trustedSourcesTypes,
      trustedSourcesColumns: [
        {
          id: 'source',
          title: this.$t('proxyTemplate.configPage.trustedSources.source'),
          fieldNames: ['source'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
          classes: 'source',
          displayFunction: (entry) => {
            if (entry.source && entry.source !== '') {
              return trustedSourcesTypes[entry.source];
            }
            return trustedSourcesTypes[defaultTrustedSourcesType];
          },
        },
        {
          id: 'sourceValue',
          title: this.$t('proxyTemplate.configPage.trustedSources.sourceValue'),
          fieldNames: ['address', 'globalFilter'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
          displayFunction: this.getTrustedSourceValue.bind(this),
        },
        {
          id: 'comment',
          title: this.$t('proxyTemplate.configPage.trustedSources.comment'),
          fieldNames: ['comment'],
          isSortable: true,
          isSearchable: true,
          cellContentClasses: 'ellipsis',
        },
      ] as RbzTableColumnOptions[],
      trustedSourcesRowButtons: [
        {
          id: 'edit',
          tooltip: this.$t('common.operations.edit.tableTooltip'),
          icon: 'IconEdit',
          editInline: true,
        },
        {
          id: 'delete',
          tooltip: this.$t('common.operations.delete.tableTooltip'),
          icon: 'IconDelete',
        },
      ] as RbzTableRowButton[],

      defaultNewAdvancedConfigEntry: {
        name: '',
        description: '',
        protocol: ['http'],
        configuration: '',
      } as ProxyTemplateAdvancedConfigEntry,
      advancedConfigColumns: [
        {
          id: 'name',
          title: this.$t('proxyTemplate.configPage.advancedConfig.nameTableTitle'),
          fieldNames: ['name'],
          classes: 'width-260px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'protocol',
          title: this.$t('proxyTemplate.configPage.advancedConfig.protocol'),
          fieldNames: ['protocol'],
          displayFunction: (entry: ProxyTemplateAdvancedConfigEntry) => {
            return entry['protocol'].sort().join(' + ').toUpperCase() || '';
          },
          classes: 'width-260px',
        },
        {
          id: 'description',
          title: this.$t('proxyTemplate.configPage.advancedConfig.description'),
          fieldNames: ['description'],
          cellContentClasses: 'ellipsis',
        },
      ] as RbzTableColumnOptions[],
      advancedConfigRowButtons: [
        {
          id: 'delete',
          tooltip: this.$t('common.operations.delete.tableTooltip'),
          icon: 'IconDelete',
        },
      ] as RbzTableRowButton[],
    };
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, usePlanetStore, useUserStore),

    entityName(): DocumentName {
      return {
        plural: this.$t('proxyTemplate.plural'),
        singular: this.$t('proxyTemplate.singular'),
      };
    },

    isLoading(): boolean {
      return (
        this.pageLoadingCounter === null || this.pageLoadingCounter > 0 || this.isDeleteLoading
      );
    },

    entityNotSaved(): boolean {
      return (
        !_.isEqual(this.selectedProxyTemplateData, this.originalProxyTemplateData) ||
        !_.isEqual(this.trustedSourcesData, this.originalTrustedSourcesData)
      );
    },

    selectedProxyTemplateProtected(): boolean {
      return this.selectedProxyTemplate?.startsWith('__');
    },

    selectedProxyTemplateReferenced(): boolean {
      return this.referencedIds.includes(this.selectedProxyTemplate);
    },

    deleteTooltip(): string {
      if (this.selectedProxyTemplateProtected) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      if (this.selectedProxyTemplateReferenced) {
        return this.$t('common.operations.delete.disabledTooltip.referenced', this.entityName);
      }
      return '';
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    clientBodyBufferSizeValid(): boolean {
      const maxValue = Number(ENV.VITE_MAX_CLIENT_BODY_BUFFER_SIZE) || 10;
      return (
        this.selectedProxyTemplateData.client_body_buffer_size &&
        Number(this.selectedProxyTemplateData.client_body_buffer_size) <= maxValue
      );
    },

    isProxyTemplateValid(): boolean {
      const trimmedName = this.selectedProxyTemplateData.name.toLowerCase().trim();
      const nameExists = this.dropdownData.filter((item) => {
        return item.name.toLowerCase().trim() === trimmedName;
      }).length;
      const nameValid =
        this.selectedProxyTemplateData.name &&
        (trimmedName === this.originalProxyTemplateData.name.toLowerCase().trim() || !nameExists);

      // Advanced configuration validity
      const advancedConfigNames = _.map(
        this.selectedProxyTemplateData['advanced_configuration'],
        (entry) => {
          return entry.name.trim();
        }
      );
      const advancedConfigNamesUnique =
        _.uniq(advancedConfigNames).length === advancedConfigNames.length;
      const advancedConfigValid =
        _.every(this.selectedProxyTemplateData['advanced_configuration'], (entry) => {
          const entryNameValid = !!entry.name;
          const entryConfigValid = !!entry.configuration;
          return entryNameValid && entryConfigValid;
        }) && advancedConfigNamesUnique;

      return nameValid && this.clientBodyBufferSizeValid && advancedConfigValid;
    },
    globalFiltersLookup(): Record<string, string> {
      return Object.fromEntries(this.globalFilters.map((item) => [item.id, item.name]));
    },
    xffHeaderNames: {
      get(): string {
        return Array.from(this.selectedProxyTemplateData?.['xff_header_name'] || '').join('\n');
      },
      set(newValue: string) {
        const value = newValue.replaceAll(' ', '');
        this.selectedProxyTemplateData['xff_header_name'] = value.split('\n');
      },
    },
  },
  methods: {
    getTrustedSourceValue(entry: TrustedSource): string {
      if (entry.source && entry.source !== 'ip') {
        return this.globalFiltersLookup[entry.globalFilter] ?? '';
      }
      return entry.address;
    },
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    redirectToList() {
      this.navigateTo(this.getListPath('proxy-templates'));
    },

    switchProxyTemplate(proxyTemplateId: string) {
      this.navigateTo(this.getEditPath('proxy-templates', proxyTemplateId));
    },

    async loadDropdownData() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/proxy-templates`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.dropdownData = Utils.sortArrayByName(response?.data?.items) as BasicDocument[];
      this.setPageLoadingStatus(false);
    },

    newProxyTemplate(): ProxyTemplate {
      const factory = DatasetsUtils.newEntityFactory['proxy-templates'];
      return factory && factory();
    },

    async loadProxyTemplate(proxyTemplateId: string) {
      this.setPageLoadingStatus(true);
      this.selectedProxyTemplate = proxyTemplateId;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/proxy-templates/${proxyTemplateId}`,
      });

      const apiData = response?.data || {};
      let xffHeaderName: string[] = [];
      // check xff_header_name and normalize api value for backwards compatibility
      if (apiData.xff_header_name) {
        if (Array.isArray(apiData.xff_header_name)) {
          xffHeaderName = apiData.xff_header_name;
        } else {
          xffHeaderName = [apiData.xff_header_name];
        }
      }

      this.selectedProxyTemplateData = {
        ...apiData,
        xff_header_name: xffHeaderName,
      };
      _.forEach(this.selectedProxyTemplateData['advanced_configuration'], (entry) => {
        entry.id = DatasetsUtils.generateUUID2();
      });
      this.originalProxyTemplateData = _.cloneDeep(this.selectedProxyTemplateData);
      this.setPageLoadingStatus(false);
    },

    async duplicateProxyTemplate() {
      this.setPageLoadingStatus(true);
      const proxyTemplateToAdd = _.cloneDeep(this.selectedProxyTemplateData);
      proxyTemplateToAdd.id = DatasetsUtils.generateUUID2();
      proxyTemplateToAdd.name = Utils.generateUniqueEntityName(
        proxyTemplateToAdd.name,
        _.map(this.dropdownData, 'name'),
        true
      );
      if (proxyTemplateToAdd['advanced_configuration']) {
        _.forEach(proxyTemplateToAdd['advanced_configuration'], (entry) => {
          delete entry.id;
        });
      }
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      const url = `conf/${this.selectedBranch}/proxy-templates/${proxyTemplateToAdd.id}`;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: proxyTemplateToAdd,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.switchProxyTemplate(proxyTemplateToAdd.id);
      }
      this.setPageLoadingStatus(false);
    },

    downloadProxyTemplate() {
      Utils.downloadFile('proxy-template', 'json', this.selectedProxyTemplateData);
    },

    openDeleteProxyTemplateModal() {
      this.showDeleteProxyTemplateModal = true;
    },

    async deleteProxyTemplate() {
      this.isDeleteLoading = true;
      const proxyTemplateId = this.selectedProxyTemplate;
      const dropdownIndex = _.findIndex(this.dropdownData, (item) => {
        return item.id === proxyTemplateId;
      });
      this.dropdownData.splice(dropdownIndex, 1);
      this.selectedProxyTemplate = null;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/proxy-templates/${proxyTemplateId}`,
        successMessage,
        failureMessage,
      });
      this.redirectToList();
      this.isDeleteLoading = false;
    },

    async saveChanges() {
      this.setPageLoadingStatus(true);
      let successMessage;
      let failureMessage;
      if (this.isNew) {
        successMessage = this.$t('common.operations.new.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.new.messages.failure', this.entityName);
      } else {
        successMessage = this.$t('common.operations.save.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.save.messages.failure', this.entityName);
      }
      this.selectedProxyTemplateData.name = this.selectedProxyTemplateData.name.trim();
      const proxyTemplateToSave = _.cloneDeep(this.selectedProxyTemplateData);
      _.forEach(proxyTemplateToSave['advanced_configuration'], (entry) => {
        delete entry.id;
        entry.name = entry.name.trim();
      });
      proxyTemplateToSave.xff_header_name = proxyTemplateToSave.xff_header_name.filter(
        (name) => name.trim() !== ''
      );
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: this.isNew ? 'POST' : 'PUT',
        url: `conf/${this.selectedBranch}/proxy-templates/${this.selectedProxyTemplateData.id}`,
        data: proxyTemplateToSave,
        successMessage,
        failureMessage,
        displayServerFailureMessage: true,
      });
      if (response) {
        await this.saveTrustedSources();
        if (this.isNew) {
          const currentRoute = this.$route.path;
          const targetRoute = currentRoute.replace(
            'new',
            `config/${this.selectedProxyTemplateData.id}`
          );
          this.$router.push(targetRoute);
        } else {
          await this.loadDropdownData();
          this.originalProxyTemplateData = _.cloneDeep(this.selectedProxyTemplateData);
        }
      }
      this.setPageLoadingStatus(false);
    },

    async loadReferencedIds() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/server-groups`,
      });
      const serverGroups = response?.data?.items || [];
      const referencedIds: string[] = [];
      _.forEach(serverGroups, (serverGroup) => {
        referencedIds.push(serverGroup['proxy_template']);
      });
      this.referencedIds = _.uniq(referencedIds);
      this.setPageLoadingStatus(false);
    },

    // TODO to switch `trusted sources` to the correct path under Proxy Template once changed in BE
    async loadTrustedSources() {
      const url = `conf/${this.selectedBranch}/planets`;
      const methodName = 'GET';
      const host = this.userPlanet;
      const response = await RequestsUtils.sendRequest({ methodName, url, host });
      const data = response?.data || {};
      this.planet = data;
      this.trustedSourcesData = _.map(data.trusted_nets, (trusted: ApiTrustedSource) => {
        return {
          id: DatasetsUtils.generateUUID2(),
          source: trusted.source || this.defaultTrustedSourcesType,
          address: trusted.address || '',
          globalFilter: trusted.gf_id || '',
          comment: trusted.comment,
        };
      });
      this.originalTrustedSourcesData = _.cloneDeep(this.trustedSourcesData);
    },

    async loadGlobalFilters() {
      this.setPageLoadingStatus(true);
      const url = `conf/${this.selectedBranch}/global-filters`;
      const fieldNames = ['name', 'rule'];
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url,
        config: { headers: { 'x-fields': `id, ${_.uniq(fieldNames).join(', ')}` } },
      });
      const items = response?.data?.items || [];
      // get only global filters with ip-entries or that are already in use
      this.globalFilters = Utils.sortArrayByName(
        items.filter((item: GlobalFilter) => {
          const allEntriesAreIP = item.rule.entries.every(
            (entry) => Object.values(entry)[0] === 'ip'
          );
          const globalFilterInUse = this.trustedSourcesData.some(
            (trusted_net) =>
              trusted_net.source === 'global_filter' && trusted_net.globalFilter === item.id
          );
          return allEntriesAreIP || globalFilterInUse;
        })
      ) as GlobalFilter[];
      this.setPageLoadingStatus(false);
    },

    getGlobalFiltersDropdownOptions(data: GenericObject) {
      const rowData = this.mapGenericObjectToTrustedSource(data);
      const usedIds = this.trustedSourcesData
        .filter((trusted_net) => trusted_net.source === 'global_filter')
        .map((trusted_net) => trusted_net.globalFilter);
      const globalFilters: [string, string][] = this.globalFilters.map((item) => [
        item.id,
        item.name,
      ]);
      return globalFilters.filter(([id]) => {
        return id === rowData.globalFilter || !usedIds.includes(id);
      });
    },

    async saveTrustedSources() {
      const trustedSourcesFailureMessage = this.$t('common.operations.save.messages.failure', {
        singular: 'Trusted Sources section',
      });
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'PUT',
        data: {
          ...this.planet,
          trusted_nets: this.trustedSourcesData.map((trusted) => {
            const trustedNet: ApiTrustedSource = {
              source: trusted.source,
              comment: trusted.comment,
            };
            if (trusted.source === 'ip') {
              trustedNet.address = trusted.address;
            } else {
              trustedNet.gf_id = trusted.globalFilter;
            }
            return trustedNet;
          }),
        },
        url: `conf/${this.selectedBranch}/planets/${this.planet.id}`,
        failureMessage: trustedSourcesFailureMessage,
        displayServerFailureMessage: true,
      });
      if (response) {
        this.originalTrustedSourcesData = _.cloneDeep(this.trustedSourcesData);
      }
    },

    mapGenericObjectToTrustedSource(data: GenericObject): TrustedSource {
      const trustedSource: TrustedSource = {
        id: data.id ?? '',
        source: data.source ?? '',
        address: data.address ?? '',
        globalFilter: data.globalFilter ?? '',
        comment: data.comment ?? '',
      };

      return trustedSource;
    },

    addTrustedSource(data: GenericObject) {
      const newTrustedSource = this.mapGenericObjectToTrustedSource(data);
      if (newTrustedSource.id === '') {
        newTrustedSource.id = DatasetsUtils.generateUUID2();
      }
      this.trustedSourcesData.push(newTrustedSource);
      this.cancelAddTrustedSource();
    },

    editTrustedSource(data: GenericObject) {
      const editTrustedSource = this.mapGenericObjectToTrustedSource(data);
      const trustedSource = _.find(this.trustedSourcesData, (trustedSource) => {
        return trustedSource.id === editTrustedSource.id;
      });
      trustedSource.source = editTrustedSource.source;
      trustedSource.globalFilter = editTrustedSource.globalFilter;
      trustedSource.address = editTrustedSource.address;
      if (trustedSource.source === 'ip') {
        trustedSource.globalFilter = '';
      } else {
        trustedSource.address = '';
      }
      trustedSource.comment = editTrustedSource.comment;
      this.cancelEditTrustedSource(trustedSource.id);
    },

    deleteTrustedSource(data: GenericObject) {
      const trustedSource = this.mapGenericObjectToTrustedSource(data);
      this.trustedSourcesData = this.trustedSourcesData.filter(
        (trusted) => trusted.id !== trustedSource.id
      );
    },

    getErrorsTrustedSourceIP(data: GenericObject) {
      const trustedSourceToCheck = this.mapGenericObjectToTrustedSource(data);
      const ip = trustedSourceToCheck.address;
      const splitValue = ip.split('/');
      const isIP = Utils.isIP(splitValue[0]);
      if (!isIP) {
        return this.$t('proxyTemplate.configPage.trustedSources.errors.invalidIp');
      }
      let isCidr = true;
      if (splitValue[1]) {
        const ipv4 = Utils.isIPv4(splitValue[0]);
        try {
          if (ipv4) {
            isCidr = ipaddr.IPv4.networkAddressFromCIDR(ip).octets.join('.') === splitValue[0];
          } else {
            const normalizedIP = ipaddr.parse(splitValue[0]).toNormalizedString();
            isCidr =
              _.map(ipaddr.IPv6.networkAddressFromCIDR(ip).parts, (part) => {
                return part.toString(16);
              }).join(':') === normalizedIP;
          }
        } catch {
          isCidr = false;
        }
      }
      if (!isCidr || trustedSourceToCheck.address.endsWith('/')) {
        return this.$t('proxyTemplate.configPage.trustedSources.errors.invalidCIDR');
      }
      const isDuplicate = this.trustedSourcesData.some((trustedSource) => {
        return (
          trustedSource.address === trustedSourceToCheck?.address &&
          trustedSource.id !== trustedSourceToCheck?.id
        );
      });
      if (isDuplicate) {
        return this.$t('proxyTemplate.configPage.trustedSources.errors.duplicateIp');
      }
      return '';
    },

    validateTrustedSourceIP(data: GenericObject) {
      const trustedSourceToCheck = this.mapGenericObjectToTrustedSource(data);
      return this.getErrorsTrustedSourceIP(trustedSourceToCheck) === '';
    },

    addAdvancedConfigEntry() {
      const newEntry = _.cloneDeep(this.defaultNewAdvancedConfigEntry);
      newEntry.id = DatasetsUtils.generateUUID2();
      this.selectedProxyTemplateData['advanced_configuration'].push(newEntry);
      this.$refs.advancedConfigurationTable.toggleExpander(newEntry.id);
      nextTick(() => {
        this.$refs[`advancedConfigurationEntryName${newEntry.id}`]?.focus();
      });
    },

    mapGenericObjectToProxyTemplateAdvancedConfigEntry(
      data: GenericObject
    ): ProxyTemplateAdvancedConfigEntry {
      const configEntry: ProxyTemplateAdvancedConfigEntry = {
        id: data.id ?? '',
        name: data.name ?? '',
        description: data.description ?? '',
        protocol: data.protocol ?? [],
        configuration: data.configuration ?? '',
      };
      return configEntry;
    },

    deleteAdvancedConfigEntry(data: GenericObject) {
      const entryToDelete: ProxyTemplateAdvancedConfigEntry =
        this.mapGenericObjectToProxyTemplateAdvancedConfigEntry(data);
      const newConfiguration = this.selectedProxyTemplateData['advanced_configuration'].filter(
        (entry) => {
          return entry.id !== entryToDelete.id;
        }
      );
      this.selectedProxyTemplateData['advanced_configuration'] = _.cloneDeep(newConfiguration);
    },

    advancedConfigEntryNameError(data: GenericObject) {
      const entry: ProxyTemplateAdvancedConfigEntry =
        this.mapGenericObjectToProxyTemplateAdvancedConfigEntry(data);
      const name = entry.name.trim();
      if (!name) {
        return this.$t('proxyTemplate.configPage.advancedConfig.errors.emptyName');
      }
      const isNameUnique =
        this.selectedProxyTemplateData['advanced_configuration'].filter((entry) => {
          return entry.name.trim() === name;
        }).length === 1;
      if (!isNameUnique) {
        return this.$t('proxyTemplate.configPage.advancedConfig.errors.duplicateName');
      }
      return '';
    },

    advancedConfigEntryCodeError(data: GenericObject) {
      const entry: ProxyTemplateAdvancedConfigEntry =
        this.mapGenericObjectToProxyTemplateAdvancedConfigEntry(data);
      const code = entry.configuration.trim();
      if (!code) {
        return this.$t('proxyTemplate.configPage.advancedConfig.errors.emptyCustomConfiguration');
      }
      return '';
    },

    isProtocolChecked(
      currentValue: ProxyTemplateAdvancedConfigEntry['protocol'],
      checkedValue: ProxyTemplateAdvancedConfigEntry['protocol']
    ) {
      return _.isEqual(currentValue, checkedValue);
    },

    setSessionDataWatcher() {
      this.$watch(
        () => [this.selectedProxyTemplateData, this.trustedSourcesData, this.entityNotSaved],
        (params) => {
          const [proxyTemplateData, trustedSourcesData, entityNotSaved] = params;
          const formData = {
            proxyTemplateData: proxyTemplateData,
            trustedSourcesData: trustedSourcesData,
          };
          Utils.saveSessionData(formData, Boolean(entityNotSaved));
        },
        {
          deep: true,
          immediate: true,
        }
      );
    },
    newTrustedSourceError(data: GenericObject): string {
      const newEntry = this.mapGenericObjectToTrustedSource(data);
      if (newEntry.source === 'ip') {
        return this.getErrorsTrustedSourceIP(newEntry);
      }
      if (newEntry.source === 'global_filter') {
        return newEntry.globalFilter == ''
          ? this.$t('proxyTemplate.configPage.trustedSources.errors.invalidGlobalFilter')
          : '';
      }
    },
    cancelAddTrustedSource() {
      this.$refs.trustedSourcesTable.setShowNewRow(false);
    },
    cancelEditTrustedSource(id: string) {
      delete this.$refs.trustedSourcesTable.rowsInEditMode[id];
    },

    // check number of lines in string and remove lines if there are more than maxLines
    limitLines(event: InputEvent, maxLines: number) {
      const el = event.target as HTMLTextAreaElement;
      const lines = el.value.split('\n');
      if (lines.length > maxLines) {
        el.value = lines.slice(0, maxLines).join('\n');
      }
    },
  },
  created() {
    this.$watch(
      () => [this.$route.params?.['branch'], this.$route.params?.['doc_id']],
      async (routeParams) => {
        const [_newBranch, newId] = routeParams;
        const routeName: string = this.normalizeRouteName(this.$route.name as string);
        if (['New Proxy Template', 'Proxy Templates Editor'].includes(routeName)) {
          this.setPageLoadingStatus(true);
          this.isNew = routeName.startsWith('New');
          await this.branchesStore.getBranches();
          await this.loadDropdownData();
          if (this.isNew) {
            this.selectedProxyTemplateData = this.newProxyTemplate();
            this.selectedProxyTemplateData.name = Utils.generateUniqueEntityName(
              this.selectedProxyTemplateData.name,
              _.map(this.dropdownData, 'name')
            );
            this.originalProxyTemplateData = _.cloneDeep(this.selectedProxyTemplateData);
          } else {
            const proxyTemplateId = newId as string;
            const dataExists = this.dropdownData.some((item) => {
              return item.id === proxyTemplateId;
            });
            await this.loadReferencedIds();
            if (dataExists) {
              await this.loadProxyTemplate(proxyTemplateId);
            } else {
              this.redirectToList();
              return;
            }
          }
          const savedSessionData = sessionStorage.getItem('formData');
          if (savedSessionData) {
            this.selectedProxyTemplateData = JSON.parse(savedSessionData).proxyTemplateData;
            this.trustedSourcesData = JSON.parse(savedSessionData).trustedSourcesData;
          }
          await this.loadTrustedSources();
          await this.loadGlobalFilters();
          this.setSessionDataWatcher();
          this.setPageLoadingStatus(false);
        }
      },
      {
        deep: true,
        immediate: true,
      }
    );
  },
  beforeRouteLeave() {
    Utils.clearSessionData();
  },
});
</script>
<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

.settings-fields-flex-box {
  $gap: 32px;

  display: flex;
  flex-wrap: wrap;
  gap: $gap;

  .settings-field {
    margin-bottom: 0;
    width: calc(50% - ($gap / 2));
  }
}

.protocol-radio-wrapper {
  display: flex;
  gap: 16px;
}

.rbz-field-info {
  svg:focus {
    outline: none;
  }
}
</style>
